import { useState } from "react";
import { Plus, X } from "lucide-react";
import { useExercises, useCreateExercise, type Exercise } from "@/hooks/useApi";

const MUSCLE_GROUPS = ["chest", "back", "legs", "shoulders", "arms", "core", "cardio"];

export default function ExercisesPage() {
  const { data: exercises = [], isLoading } = useExercises();
  const createMutation = useCreateExercise();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newGroup, setNewGroup] = useState("chest");

  const filtered = exercises.filter(
    (e) =>
      e.name.toLowerCase().includes(search.toLowerCase()) &&
      (filter ? e.muscle_group === filter : true)
  );

  const grouped = filtered.reduce<Record<string, Exercise[]>>((acc, ex) => {
    (acc[ex.muscle_group] ??= []).push(ex);
    return acc;
  }, {});

  async function addCustom() {
    if (!newName.trim()) return;
    await createMutation.mutateAsync({ name: newName.trim(), muscle_group: newGroup });
    setNewName("");
    setShowForm(false);
  }

  if (isLoading) return <div className="p-4 text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="p-4 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Exercises</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1 rounded-lg bg-zinc-800 px-3 py-2 text-sm hover:bg-zinc-700 transition-colors"
        >
          {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showForm ? "Cancel" : "Custom"}
        </button>
      </div>

      {showForm && (
        <div className="rounded-lg border border-zinc-800 p-3 space-y-3">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Exercise name"
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-zinc-600"
          />
          <select
            value={newGroup}
            onChange={(e) => setNewGroup(e.target.value)}
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:ring-1 focus:ring-zinc-600"
          >
            {MUSCLE_GROUPS.map((g) => (
              <option key={g} value={g}>
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </option>
            ))}
          </select>
          <button
            onClick={addCustom}
            disabled={!newName.trim()}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-zinc-950 disabled:opacity-40"
          >
            Add Exercise
          </button>
        </div>
      )}

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search…"
        className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-zinc-600"
      />

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter("")}
          className={`rounded-full px-3 py-1 text-xs transition-colors ${
            filter === "" ? "bg-zinc-50 text-zinc-950" : "bg-zinc-800 text-zinc-400"
          }`}
        >
          All
        </button>
        {MUSCLE_GROUPS.map((g) => (
          <button
            key={g}
            onClick={() => setFilter(g === filter ? "" : g)}
            className={`rounded-full px-3 py-1 text-xs capitalize transition-colors ${
              filter === g ? "bg-zinc-50 text-zinc-950" : "bg-zinc-800 text-zinc-400"
            }`}
          >
            {g}
          </button>
        ))}
      </div>

      <div>
        {Object.entries(grouped).map(([group, exs]) => (
          <div key={group}>
            <p className="py-2 text-xs font-medium uppercase tracking-wider text-zinc-500">
              {group}
            </p>
            <ul className="space-y-1">
              {exs.map((ex) => (
                <li
                  key={ex.id}
                  className="flex items-center justify-between rounded-lg border border-zinc-800 px-3 py-2"
                >
                  <span className="text-sm">{ex.name}</span>
                  {ex.is_custom === 1 && (
                    <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded-full">
                      custom
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
